import { api } from '@/api/api';

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export const isValidProfileId = (id: string | null | undefined): id is string =>
    !!id && id !== EMPTY_GUID;

export interface EmployeePickerItem {
    id: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    profilePictureUrl?: string | null;
    departmentId?: string;
    jobTitleId?: string;
    employeeLevelId?: string;
    companyAddressId?: string;
    isActive: boolean;
    isAlreadyAssigned: boolean;
}

export interface EmployeePickerResponse {
    pageNumber: number;
    pageSize: number;
    itemCount: number;
    totalCount: number;
    hasNextPage: boolean;
    items: EmployeePickerItem[];
}

// The attribute-id filters shared with RosterFilterParams (see below), also accepted by the
// /EmployeeDirectory/employees endpoint that backs the "individual assignment" employee picker.
export interface EmployeePickerFilterParams {
    departmentIds?: string[];
    jobTitleIds?: string[];
    managerIds?: string[];
    companyAddressIds?: string[];
    employeeLevelIds?: string[];
    customAttributeIds?: string[];
}

export interface EmployeePickerParams extends EmployeePickerFilterParams {
    pageNumber?: number;
    pageSize?: number;
    searchQuery?: string;
    isActiveOnly?: boolean;
    includeInvited?: boolean;
    competencyId?: string;
    groupId?: string;
}

export interface ProfileAssignmentPayload {
    employeeIds: string[];
    // When false, employees already holding a different profile are skipped and
    // reported in the response instead of being moved. Defaults to true server-side.
    overwriteExisting?: boolean;
}

export interface AssignProfileSkippedConflict {
    employeeId: string;
    currentProfileId: string;
}

// Assignment outcome report returned by the assign endpoint.
export interface AssignProfileResult {
    assignments: ProfileAssignment[];
    assignedCount: number;
    unchangedCount: number;
    skippedConflicts: AssignProfileSkippedConflict[];
}

// The roster filter params mirror the company-attribute filters built by
// `buildFilterParams` (see EmployeeRuleSelector). They are sent to the backend as
// PascalCase array query params (DepartmentIds[], JobTitleIds[], …).
export interface RosterFilterParams {
    departmentIds?: string[];
    jobTitleIds?: string[];
    managerIds?: string[];
    companyAddressIds?: string[];
    employeeLevelIds?: string[];
    customAttributeIds?: string[];
    searchQuery?: string;
    isActiveOnly?: boolean;
    includeInvited?: boolean;
}

// One roster row. Shape mirrors EmployeePickerItem plus the currently assigned
// profile (id + multilingual name), as returned by the roster endpoint.
export interface RosterEmployee {
    id: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    profilePictureUrl?: string | null;
    departmentId: string | null;
    jobTitleId: string | null;
    employeeLevelId: string | null;
    companyAddressId: string | null;
    isActive: boolean;
    assignedProfileId: string | null;
    assignedProfileName: Record<string, string> | null;
    // This employee's per-employee competency overlay (added/removed competency ids).
    // Used to pre-seed the selector so editing an assignment doesn't wipe prior
    // customizations. Always present (empty arrays when there is no overlay).
    addedCompetencyIds: string[];
    removedCompetencyIds: string[];
}

export interface ProfileAssignment {
    id: string;
    employeeId: string;
    competencyProfileId: string;
}

export interface ProfileAssignmentCount {
    profileId: string;
    count: number;
}

export default {
    async getEmployees(params: EmployeePickerParams): Promise<EmployeePickerResponse> {
        const response = await api.get('/EmployeeDirectory/employees', {
            params: {
                PageNumber: params.pageNumber ?? 1,
                PageSize: params.pageSize ?? 100,
                SearchQuery: params.searchQuery || undefined,
                IsActiveOnly: params.isActiveOnly,
                IncludeInvited: params.includeInvited,
                DepartmentIds: params.departmentIds,
                JobTitleIds: params.jobTitleIds,
                ManagerIds: params.managerIds,
                CompanyAddressIds: params.companyAddressIds,
                EmployeeLevelIds: params.employeeLevelIds,
                CustomAttributeIds: params.customAttributeIds,
                CompetencyId: params.competencyId || undefined,
                GroupId: params.groupId || undefined,
            },
        });

        return response.data;
    },

    async assignProfile(
        profileId: string,
        payload: ProfileAssignmentPayload,
    ): Promise<AssignProfileResult> {
        const response = await api.post(
            `/EmployeeCompetencyProfileAssignment/${profileId}/assign`,
            payload,
        );

        return response.data;
    },

    // The employee-centric roster: every company employee with their currently
    // assigned profile (or null). No server pagination — the page splits the flat
    // array into the two sections and paginates each client-side.
    async getRoster(params: RosterFilterParams = {}): Promise<RosterEmployee[]> {
        const response = await api.get<RosterEmployee[]>(
            '/EmployeeCompetencyProfileAssignment/roster',
            {
                params: {
                    DepartmentIds: params.departmentIds,
                    JobTitleIds: params.jobTitleIds,
                    ManagerIds: params.managerIds,
                    CompanyAddressIds: params.companyAddressIds,
                    EmployeeLevelIds: params.employeeLevelIds,
                    CustomAttributeIds: params.customAttributeIds,
                    SearchQuery: params.searchQuery || undefined,
                    IsActiveOnly: params.isActiveOnly,
                    IncludeInvited: params.includeInvited,
                },
            },
        );

        return response.data.map((employee) => ({
            ...employee,
            assignedProfileId: isValidProfileId(employee.assignedProfileId)
                ? employee.assignedProfileId
                : null,
        }));
    },

    async unassignProfile(profileId: string, employeeIds: string[]): Promise<void> {
        await api.post(`/EmployeeCompetencyProfileAssignment/${profileId}/unassign`, {
            employeeIds,
        });
    },

    async getAssignments(profileId: string): Promise<ProfileAssignment[]> {
        const response = await api.get(
            `/EmployeeCompetencyProfileAssignment/${profileId}/assignments`,
        );

        return response.data;
    },

    // Per-profile assigned-employee counts across the company, keyed by profile id.
    async getAssignmentStatistics(): Promise<ProfileAssignmentCount[]> {
        const response = await api.get('/EmployeeCompetencyProfileAssignment/statistics');

        return response.data;
    },
};
